import React, { useEffect } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  useLocation
} from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import { UserContextProvider } from '../context/authContext';
import { ProtectedRoute } from './ProtectedRoute';
import { LoginPage } from './Components/signin_signup/LoginPage';
import { App } from '../App';
import { DocumentContextProvider } from '../context/documentContext';
import { ProjectContextProvider } from '../context/projectContext';
import { SignupPage } from './Components/signin_signup/SignupPage';
import { AppContextProvider } from '../context/appContext';
import { AlertProvider } from '../context/alertContext';
import { NotificationProvider } from '../context/notificationContext';
import { SearchHistoryProvider } from '../context/searchHistoryContext';
import { PolicyContextProvider } from '../context/policyContext';
import SignupPageStart from './Components/signin_signup/1Signup';
import LoginPageNew from './Components/signin_signup/LoginPageNew';
import { CreateAccount } from './Components/accountViews/CreateAccount';
import CreateAccountNew from './Components/accountViews/CreateAccountNew';
import SignupComplete from './Components/accountViews/signupComplete';
import ResetPassword from './Components/signin_signup/resetPassword';
import { recordRumPageView } from '../hooks/rum';

// Wraps children with all your providers
function withProviders(children: React.ReactNode) {
  return (
    <UserContextProvider>
      <AlertProvider>
        <Toaster
          position="bottom-center"
          gutter={10}
          containerStyle={{
            bottom: 42
          }}
          toastOptions={{
            duration: 4000,
            style: {
              background: '#ffffff',
              color: '#0f172a',
              border: '1px solid #e2e8f0',
              borderRadius: '14px',
              padding: '12px 14px',
              boxShadow: '0 18px 48px rgba(15, 23, 42, 0.12)'
            },
            success: {
              iconTheme: {
                primary: '#56dac5',
                secondary: '#ffffff'
              }
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#ffffff'
              }
            }
          }}
        />
        <SearchHistoryProvider>
          <AppContextProvider>
            <ProjectContextProvider>
              <DocumentContextProvider>
                <PolicyContextProvider>
                  <NotificationProvider>{children}</NotificationProvider>
                </PolicyContextProvider>
              </DocumentContextProvider>
            </ProjectContextProvider>
          </AppContextProvider>
        </SearchHistoryProvider>
      </AlertProvider>
    </UserContextProvider>
  );
}

const PageViewTracker: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    try {
      recordRumPageView({
        pageId: location.pathname + location.search,
        pageAttributes: { template: 'app-shell' }
      });
    } catch (e) {
      // no-op
    }
  }, [location.pathname, location.search]);
  return null;
};

const router = createBrowserRouter([
  {
    path: '/login',
    element: withProviders(
      <>
        <PageViewTracker />
        <LoginPageNew />
      </>
    )
  },
  {
    path: '/signup',
    element: withProviders(
      <>
        <PageViewTracker />
        <SignupPageStart />
      </>
    )
  },
  {
    path: '/reset-password',
    element: withProviders(
      <>
        <PageViewTracker />
        <ResetPassword />
      </>
    )
  },
  {
    element: withProviders(
      <>
        <PageViewTracker />
        <ProtectedRoute />
      </>
    ),
    children: [
      {
        path: '/account-new',
        element: <CreateAccountNew />
      },
      {
        path: '/confirmation',
        element: <SignupComplete />
      },
      {
        path: '/app/*',
        element: <App />
      },
      {
        path: '*',
        element: <Navigate to="/app/landing" replace />
      }
    ]
  }
]);

export const Routing = () => {
  return (
    <RouterProvider router={router} fallbackElement={<div>Loading...</div>} />
  );
};
